// src/team/Billing.tsx: the Billing section of Team Settings, for team admins, wherever billing is on (docs/SPEC.md
// 16.6; docs/DECISIONS.md "Billing" and "Pricing"). Every step happens here: the plan and what is due today, the payment
// form (PaymentForm.tsx), the subscription, its invoices, a new payment method, canceling and resuming. Nothing sends the
// builder to Stripe; the only mention of it is the small "Payments by Stripe" line.
//
// One plan with everything included, at two rates (shared/pricing.ts): founding ($499 a month billed yearly, the first
// 10 builders in a market, locked while the subscription stays active) and standard ($699 a month, monthly or yearly),
// The amounts shown are the ones Stripe will charge (the prices, read by lookup key).
//
// Dead simple before it is set up (the owner, 2026-10-17: a detailed billing screen was "way too confusing"; then "we
// should let them toggle between annual and monthly"): one plan line with an inline Monthly | Yearly toggle (Yearly
// first while founding is offered: founding then; without it, standard yearly); one Due Today row that follows the
// toggle, its breakdown in an (i) tip; the payment fields with the billing address as one line and Edit; one button that
// says what it pays. The server maps the toggle to the plan, so founding is never the browser's choice. Everything is
// rows on hairlines, never boxes inside boxes.
//
// States (billingState): not set up; a bank account waiting on its small deposits (the plan and the code); active (plan
// and rate, renewal or next charge, payment method with Change, invoices, Cancel Subscription); a payment problem (what
// failed, then Update Payment Method, which pays the open invoice again); canceling (the last day, Resume Subscription);
// first payment pending. The free preview (docs/DECISIONS.md "Free preview, Activate to open, 30-day money-back guarantee (owner,
// 2026-10-21)") adds three. Preview (a builder in setup): one row, "Preview · Families see “Opening soon”", and Activate, which
// goes to its own screen (src/setup/ActivateScreen.tsx; docs/DECISIONS.md "Activate is not part of setup (owner, 2026-10-22)").
// Active within 30 days of the first payment: "30-day money-back
// guarantee until November 20" and Cancel and Refund (a confirmation first; it cancels now and refunds the payment). Service
// ended (a refund, a cancel that took effect, a payment that failed): "Families can't start new boards." and Activate, which opens
// the plan choice; paying reopens everything at once. Status otherwise is information only: nothing anywhere is locked by it. Free
// until a quote's first-charge date (trialing): no status pill, and First payment says it as the quote did, "Free until November 9,
// 2026" then "$5,988 a year plus sales tax" until the tax is known. Free (docs/DECISIONS.md "Free while Mark tries it (owner,
// 2026-10-28)"; a builder that accepted the owner's free quote and has no subscription): one row, Plan, Free, "We'll agree on a price
// together before anything is charged.", and nothing else: no toggle, no Due Today, no payment fields, no Activate, no agreement
// checkbox. Root class: hb-billing. Copy: plain, no em dash, Title Case buttons, and never a future price.
import type { ComponentChildren } from "preact";
import { useEffect, useId, useRef, useState } from "preact/hooks";
import { currentAcceptance, type AgreementId } from "../../shared/agreements";
import type {
  BillingAddress, BillingAmount, BillingCompleteResponse, BillingFounding, BillingInvoice, BillingInvoiceStatus, BillingPaymentMethod, BillingPlan,
  BillingSetupPurpose, BillingSubscribeRequest, BillingVerifyRequest, TeamBilling, TeamService,
} from "../../shared/api";
import {
  foundingSaving, monthlyRate, PLAN_SHAPE, planForToggle, todayCharge, type PlanId, type PlanInterval, type PriceAmounts,
} from "../../shared/pricing";
import { href, navigate, route } from "../app/router";
import { api } from "../lib/api";
import { formatDate, titleCase } from "../lib/format";
import { ApiError } from "../lib/http";
import { me, site } from "../state/session";
import { Button, cx, Dialog, Field, Icon, LinkButton, Pill, Sheet, Skeleton, TextInput, toast, VisuallyHidden } from "../ui";
import { AgreeBox, AgreementSheet } from "./AgreementSheet";
import { PaymentForm, type PaymentFormHandle } from "./PaymentForm";

export type BillingOn = Exclude<TeamBilling, { mode: "off" }>;
export type BillingState = "setup" | "verify" | "active" | "problem" | "canceling" | "pending";

/**
 * Which screen a billing answer is. No subscription (or one that ended): set up, or verify while a bank account waits
 * on its deposits. A failed payment that stands: problem. Ending at the period's end: canceling. The first payment
 * still on its way: pending. Anything else that stands, a status Stripe adds later included: active.
 */
export function billingState(b: BillingOn): BillingState {
  const standing = b.status !== null && b.status !== "canceled" && b.status !== "incomplete_expired";
  if (!standing) return b.verification ? "verify" : "setup";
  if (b.status === "past_due" || b.status === "unpaid" || b.problem) return "problem";
  if (b.cancelAtPeriodEnd) return "canceling";
  if (b.status === "incomplete") return "pending";
  return "active";
}

// ------------------------------------------------------------------------------------------------------- words

const moneyFormats = new Map<string, Intl.NumberFormat>();
/** $1,889.38; with `whole`, $699 when there are no cents. */
export function money(cents: number, currency = "usd", whole = false): string {
  const fraction = whole && cents % 100 === 0 ? 0 : 2;
  const key = `${currency}:${fraction}`;
  let f = moneyFormats.get(key);
  if (!f) {
    f = new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase(), minimumFractionDigits: fraction, maximumFractionDigits: fraction });
    moneyFormats.set(key, f);
  }
  return f.format(cents / 100);
}

const BRANDS: Readonly<Record<string, string>> = {
  visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover", diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay",
};

/** "Chase ending 6789", "Visa ending 4242". */
export function methodName(pm: BillingPaymentMethod): string {
  if (pm.kind === "bank") return `${pm.bank ?? "Bank account"} ending ${pm.last4}`;
  return `${BRANDS[pm.brand] ?? titleCase(pm.brand)} ending ${pm.last4}`;
}

/** "Bank account", "Expires 08/27". */
export function methodMeta(pm: BillingPaymentMethod): string {
  if (pm.kind === "bank") return "Bank account";
  return `Expires ${String(pm.expMonth).padStart(2, "0")}/${String(pm.expYear).slice(-2)}`;
}

const INVOICE_STATUS: Readonly<Record<BillingInvoiceStatus, string>> = { paid: "Paid", open: "Pending", void: "Void", uncollectible: "Unpaid" };

/** "Founding" or "Standard". */
export function planName(plan: PlanId): string {
  return PLAN_SHAPE[plan].kind === "founding" ? "Founding" : "Standard";
}

/** A plan's rate in words: "$499 a month, billed yearly", "$699 a month, billed monthly". `amount` is per period, in cents. */
export function rateWords(interval: "month" | "year", amount: number, currency = "usd"): string {
  return interval === "year"
    ? `${money(Math.round(amount / 12), currency, true)} a month, billed yearly`
    : `${money(amount, currency, true)} a month, billed monthly`;
}

/**
 * What a quote's free time turns into, as the quote page says it: "$5,988 a year plus sales tax" until the tax is known, then
 * "$6,467.04 a year, with $479.04 tax" ("$5,988 a year" when it comes to none). Whole dollars without cents. `next` is the first
 * charge as Stripe has it lined up; without one, `amount` (the plan's price for one period, in cents).
 */
export function afterFree(next: BillingAmount | null, amount: number, period: "month" | "year", currency = "usd"): string {
  if (!next || next.tax === null) return `${money(next?.total ?? amount, currency, true)} a ${period} plus sales tax`;
  return `${money(next.total, currency, true)} a ${period}${next.tax ? `, with ${money(next.tax, currency)} tax` : ""}`;
}

/**
 * What is due today in words, and on the button: the plan's first period, plus tax. Without sales tax: "$5,988", "Pay $5,988
 * and Activate". With sales tax known from the address: "$5,988 plus $479.04 tax", "Pay $6,467.04 and Activate". With sales tax
 * not known yet: "$5,988 plus sales tax", "Pay $5,988 Plus Tax and Activate". `finish` is the button's last words.
 */
export function dueWords(today: number, due: BillingAmount | null, tax: boolean, finish = "Activate"): { text: string; button: string } {
  const before = money(today, "usd", true);
  if (!tax) return { text: before, button: `Pay ${before} and ${finish}` };
  if (due && due.tax !== null) return { text: `${before} plus ${money(due.tax)} tax`, button: `Pay ${money(due.total, "usd", true)} and ${finish}` };
  return { text: `${before} plus sales tax`, button: `Pay ${before} Plus Tax and ${finish}` };
}

/** Where the toggle starts: Yearly while founding is offered (the market has spots and the builder never used founding), Monthly otherwise. */
export function defaultInterval(b: Pick<BillingOn, "founding">): PlanInterval {
  return b.founding.offered ? "year" : "month";
}

/** The plan the toggle stands for, as the server will map it (it decides; this is only what the screen shows). */
export function toggledPlan(b: Pick<BillingOn, "founding">, interval: PlanInterval): PlanId {
  return planForToggle(interval, b.founding.offered);
}

export function failMessage(e: unknown, fallback = "That didn't work. Try again."): string {
  return e instanceof ApiError ? e.message : fallback;
}

/** What a saved payment method came to, in a few words (the toast after the form). */
export function savedLine(done: BillingCompleteResponse): string {
  if (done.result === "verify") return "Check your bank account for a small deposit, then enter its code here.";
  if (done.result === "processing") return "The bank is still checking the account. Try again in a few minutes.";
  const retry = done.retry?.outcome;
  if (retry === "paid") return "Saved. The payment went through.";
  if (retry === "processing") return "Saved. The bank payment is on its way.";
  if (retry === "failed") return `Saved, but the payment didn't go through.${done.retry?.reason ? ` ${done.retry.reason}` : ""}`;
  return "Payment method saved.";
}

// The toggle as it was before a bank's own page (rare) sends the browser away and back: kept for this tab only.
const INTERVAL_KEY = "hb-billing-interval";
function rememberInterval(interval: PlanInterval): void {
  try {
    sessionStorage.setItem(INTERVAL_KEY, interval);
  } catch {
    // Storage blocked: the default is used on the way back.
  }
}
function rememberedInterval(): PlanInterval | null {
  try {
    const v = sessionStorage.getItem(INTERVAL_KEY);
    return v === "month" || v === "year" ? v : null;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------------------------------------- section

/** After the bank's or card company's own page (rare), Stripe comes back here with the SetupIntent. */
function takeReturn(): string | null {
  const q = route.value.query;
  if (!q.get("billing") && !q.get("setup_intent")) return null;
  const id = q.get("setup_intent");
  navigate(href("teamSettings"), { replace: true, scroll: "preserve" });
  return id && /^seti_[A-Za-z0-9]+$/.test(id) ? id : null;
}

/** Starts the toggle's plan remembered from before the bank's page (else the default) once the method is saved. */
async function startAfterReturn(): Promise<boolean> {
  let interval = rememberedInterval();
  if (!interval) {
    const b = await api.teamBilling();
    if (b.mode === "off") return false;
    interval = defaultInterval(b);
  }
  return api.billingSubscribe({ interval }).then(() => true, () => false);
}

/** The builder's billing, for team admins; nothing for other staff, or where billing is off. */
export function BillingSection() {
  const admin = me.value?.staff?.role === "team_admin";
  const [billing, setBilling] = useState<TeamBilling | null>(null);
  const [failed, setFailed] = useState(false);
  // "Activate" in the emails lands here with ?plan=1: the plan choice is open.
  const [choose] = useState(() => route.value.query.get("plan") === "1");
  useEffect(() => {
    if (choose) navigate(href("teamSettings"), { replace: true, scroll: "preserve" });
  }, []);

  const reload = async () => {
    try {
      setBilling(await api.teamBilling());
      setFailed(false);
    } catch {
      // Billing never blocks Settings: the section says so and offers Try Again (or stays hidden before a first answer).
      setFailed(true);
    }
  };

  useEffect(() => {
    if (!admin) return;
    const returned = takeReturn();
    void (async () => {
      if (returned) {
        try {
          const done = await api.billingComplete({ setupIntentId: returned });
          // A method saved for starting starts the subscription; if that fails, the method stays and so does the button.
          const started = done.result === "ready" && done.purpose === "start" && (await startAfterReturn());
          toast(started ? "Your subscription has started." : savedLine(done));
        } catch (e) {
          toast(failMessage(e));
        }
      }
      await reload();
    })();
  }, [admin]);

  if (!admin || !billing || billing.mode === "off") return null;
  return (
    <section class="ts-sec hb-billing" aria-labelledby="ts-billing">
      <div class="ts-sec-h">
        <h2 class="ts-h" id="ts-billing">
          Billing
        </h2>
        {billing.mode === "test" ? <Pill tone="line">Test Mode</Pill> : null}
      </div>
      <BillingBody billing={billing} onChanged={reload} choose={choose} />
      {billing.partial || failed ? (
        <p class="bill-partial t-meta">
          Some details didn't load.{" "}
          <button type="button" class="link-btn" onClick={() => void reload()}>
            Try Again
          </button>
        </p>
      ) : null}
    </section>
  );
}

function BillingBody(props: { billing: BillingOn; onChanged: () => Promise<void>; choose?: boolean }) {
  const b = props.billing;
  const state = billingState(b);
  if (b.free && b.status === null) return <FreeRow />;
  if ((state === "setup" || state === "verify") && b.service.state === "preview") return <PreviewRow />;
  if (state === "setup" || state === "verify") return <SetupView billing={b} onChanged={props.onChanged} service={b.service} choose={props.choose} />;
  return <SubscribedView billing={b} state={state} onChanged={props.onChanged} />;
}

/** One row on a hairline: a label, its value and a line under it, and what to do about it. */
export function Row(props: { label: string; value: ComponentChildren; sub?: ComponentChildren; extra?: ComponentChildren; end?: ComponentChildren; class?: string }) {
  return (
    <li class={props.class}>
      <span class="bill-k">{props.label}</span>
      <span class="bill-l">
        <b>{props.value}</b>
        {props.sub ? <span>{props.sub}</span> : null}
        {props.extra}
      </span>
      {props.end ? <span class="bill-end">{props.end}</span> : null}
    </li>
  );
}

// ------------------------------------------------------------------------------------------------- not set up

/** What one option shows: its name, its rate, what it charges today and the short lines. All from the prices the Worker returned. */
export type PlanOption = {
  interval: PlanInterval;
  plan: PlanId;
  name: string;
  /** The small mark on Annual while founding is offered. */
  mark: string | null;
  /** The standard rate, struck through before the founding rate. */
  was: string | null;
  price: string;
  billed: string;
  today: string;
  lines: string[];
};

/** The two options for the prices and the founding answer: Annual first, then Monthly. */
export function planOptions(amounts: PriceAmounts, founding: Pick<BillingFounding, "offered">): PlanOption[] {
  const one = (interval: PlanInterval): PlanOption => {
    const plan = planForToggle(interval, founding.offered);
    const kind = PLAN_SHAPE[plan].kind;
    const year = interval === "year";
    return {
      interval,
      plan,
      name: year ? "Annual" : "Monthly",
      mark: kind === "founding" ? "Founding" : null,
      was: kind === "founding" ? money(amounts.standard_monthly, "usd", true) : null,
      price: money(monthlyRate(amounts, plan), "usd", true),
      billed: year ? "billed yearly" : "billed monthly",
      today: money(todayCharge(amounts, plan).total, "usd", true),
      lines:
        kind === "founding"
          ? [`Save ${money(foundingSaving(amounts), "usd", true)} a year`, "Locked while you stay"]
          : [year ? "Locks your rate" : "Cancel anytime"],
    };
  };
  return [one("year"), one("month")];
}

/**
 * The plan choice (owner pick, 2026-10-20, "Side by side"): Annual and Monthly as two radio options, side by side on a
 * desktop and stacked on a phone. Each shows its rate, what it charges today and a few short lines; the chosen one has
 * the ink border. `due` is the sales-tax preview for the chosen option ("plus $479.04 tax" once known); `tax` says sales
 * tax applies at all. Used by the setup's Billing step and by Settings before a builder subscribes.
 * `when` is when that first period is charged: "today" unless a quote names a later first payment ("on November 9").
 */
export function PlanChoice(props: {
  amounts: PriceAmounts; founding: BillingFounding; interval: PlanInterval; onInterval: (v: PlanInterval) => void; tax: boolean; due: BillingAmount | null;
  when?: string;
}) {
  const id = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const options = planOptions(props.amounts, props.founding);
  const pick = (i: number) => {
    const o = options[i];
    if (!o) return;
    props.onInterval(o.interval);
    refs.current[i]?.focus();
  };
  const onKey = (e: KeyboardEvent, i: number) => {
    const next = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!next) return;
    e.preventDefault();
    pick((i + next + options.length) % options.length);
  };
  return (
    <div class="pc">
      <div class="pc-opts" role="radiogroup" aria-label="Billing">
        {options.map((o, i) => {
          const on = o.interval === props.interval;
          const taxed = on && props.due?.tax != null;
          return (
            <button
              key={o.interval}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              class={cx("pc-opt", on && "is-on")}
              aria-checked={on ? "true" : "false"}
              aria-labelledby={`${id}-n${i}`}
              aria-describedby={`${id}-d${i}`}
              tabIndex={on ? 0 : -1}
              onClick={() => pick(i)}
              onKeyDown={(e) => onKey(e, i)}
            >
              <span class="pc-dot" aria-hidden="true">
                <i />
              </span>
              <span class="pc-name" id={`${id}-n${i}`}>
                <b>{o.name}</b>
                {o.mark ? <em class="pc-mark">{o.mark}</em> : null}
              </span>
              <span class="pc-body" id={`${id}-d${i}`}>
                <span class="pc-cost">
                  <span class="pc-price">
                    {o.was ? (
                      <s class="pc-was">
                        <VisuallyHidden>was </VisuallyHidden>
                        {o.was}
                      </s>
                    ) : null}
                    <b>{o.price}</b>
                    <span class="pc-per">a month</span>
                  </span>
                  <span class="pc-billed">{o.billed}</span>
                </span>
                <span class="pc-today">
                  <b>{o.today} {props.when ?? "today"}</b>
                  {taxed ? <span>plus {money(props.due!.tax!)} tax</span> : props.tax ? <span>plus sales tax</span> : null}
                </span>
                <span class="pc-lines">
                  {o.lines.map((l) => (
                    <span key={l}>{l}</span>
                  ))}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** A builder on the owner's free quote, with no subscription: one row and no way to pay. The price is agreed together first. */
function FreeRow() {
  return (
    <ul class="bill-rows">
      <Row label="Plan" class="bill-plan-row" value="Free" sub="We'll agree on a price together before anything is charged." />
    </ul>
  );
}

/** In preview (a builder in setup): what families see, and Activate, which has its own screen. */
function PreviewRow() {
  return (
    <ul class="bill-rows">
      <Row
        label="Status"
        class="bill-plan-row bill-preview-row"
        value="Preview · Families see “Opening soon”"
        end={
          <LinkButton href={href("teamActivate")} iconAfter="arrow-r">
            Activate
          </LinkButton>
        }
      />
    </ul>
  );
}

/**
 * Not subscribed yet, after service ended: it says so ("Families can't start new boards.") with Activate, which opens the plan
 * choice (`choose` opens it at once, from an email); the button then pays and reopens everything.
 */
function SetupView(props: { billing: BillingOn; onChanged: () => Promise<void>; service: TeamService; choose?: boolean }) {
  const b = props.billing;
  const lapsed = props.service.state === "ended";
  const [open, setOpen] = useState(!lapsed || !!props.choose);
  const root = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (props.choose) root.current?.scrollIntoView?.({ block: "start" });
  }, []);
  const [interval, chooseInterval] = useState<PlanInterval>(() => defaultInterval(b));
  const plan = toggledPlan(b, interval);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [edited, setEdited] = useState<BillingAddress | null>(null);
  const [previewed, setPreview] = useState<{ plan: PlanId; amount: BillingAmount } | null>(null);
  const handle = useRef<PaymentFormHandle | null>(null);
  const ask = useRef(0);
  // The agreements: a checkbox above the button until an acceptance is on record (docs/SPEC.md 16.10).
  const needAgree = !b.agreementAccepted;
  const [agreed, setAgreed] = useState(false);
  const [doc, setDoc] = useState<AgreementId | null>(null);
  const saved = b.paymentMethod;
  // The address sales tax is figured from: the one being edited, else the one the form starts from (or the saved one).
  const address = edited ?? b.address;

  // With sales tax: today's total for the toggle's plan and the address (an edited one once it is complete and has
  // settled for a moment). An answer for a plan or an address that has changed since is dropped. (When founding closes
  // while the screen is open, the reload maps Yearly to standard yearly, and this asks again.)
  useEffect(() => {
    const n = ++ask.current;
    if (!b.tax || !address) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(
      () => {
        api.billingPreview({ interval, address }).then(
          (amount) => n === ask.current && setPreview({ plan, amount }),
          () => undefined,
        );
      },
      edited ? 500 : 0,
    );
    return () => clearTimeout(timer);
  }, [plan, address, b.tax]);

  if (!b.prices) {
    return <p class="bill-wait t-body">Billing isn't ready yet. Try again in a few minutes.</p>;
  }
  const amounts = b.prices.amounts;
  const today = todayCharge(amounts, plan).total;
  const due = dueWords(today, previewed?.plan === plan ? previewed.amount : null, b.tax, "Activate");
  const buttonWords = due.button;
  const ended = b.status === "canceled" && b.currentPeriodEnd ? formatDate(b.currentPeriodEnd, "long") : null;

  // What subscribing asks for: the toggle, and the total the page shows (the server refuses another).
  const subscribeBody = { interval, expectedTotal: today, agree: needAgree && agreed ? currentAcceptance() : undefined };
  const agreement = needAgree ? <AgreeBox checked={agreed} onChange={setAgreed} onOpen={setDoc} /> : null;
  const start = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    rememberInterval(interval);
    try {
      if (!saved) {
        const form = handle.current;
        if (!form) {
          setError("The payment form is still loading. Try again in a moment.");
          return;
        }
        const confirmed = await form.confirm();
        if (!confirmed.ok) {
          setError(confirmed.message);
          return;
        }
        const done = await api.billingComplete({ setupIntentId: confirmed.setupIntentId, address: confirmed.address });
        if (done.result !== "ready") {
          toast(savedLine(done));
          await props.onChanged();
          return;
        }
      }
      await api.billingSubscribe(subscribeBody);
      toast(lapsed ? "Your plan has started. Families can start boards again." : "Your subscription has started.");
      await props.onChanged();
    } catch (e) {
      setError(failMessage(e));
      // The total changed (the last founding spot taken meanwhile): the section shows it as it is now.
      if (e instanceof ApiError && e.status === 409) await props.onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="bill-setup" ref={root}>
      {lapsed ? (
        <div class="bill-alert is-quiet" role="status">
          <Icon name="info" />
          <div class="bill-alert-b">
            <b>Families can't start new boards.</b>
            <span>{props.service.endedCause === "refund" ? "Your plan was cancelled and refunded." : props.service.endedCause === "payment_failed" ? "The last payment didn't go through." : "Your plan has ended."}</span>
          </div>
          {open ? null : <Button onClick={() => setOpen(true)}>Activate</Button>}
        </div>
      ) : null}
      {open ? (
        <>
      <PlanChoice
        amounts={amounts}
        founding={b.founding}
        interval={interval}
        onInterval={chooseInterval}
        tax={b.tax}
        due={previewed?.plan === plan ? previewed.amount : null}
      />
      {ended ? <p class="bill-ended t-meta">Your last subscription ended {ended}.</p> : null}
      <div class="bill-pay-sec">
        {b.verification ? (
          <>
            {agreement}
            <DepositCode billing={b} onChanged={props.onChanged} start={subscribeBody} button={buttonWords} />
          </>
        ) : (
          <>
            <h3 class="bill-sub-h">Payment method</h3>
            {saved ? (
              <MethodRow pm={saved} onChange={() => setChanging(true)} />
            ) : (
              <PaymentForm purpose="start" address={b.address} onHandle={(h) => (handle.current = h)} onAddress={setEdited} />
            )}
            {error ? (
              <p class="bill-err" role="alert">
                {error}
              </p>
            ) : null}
            {agreement}
            <div class="bill-pay-go">
              <Button size="lg" onClick={() => void start()} busy={busy} disabled={needAgree && !agreed}>
                {buttonWords}
              </Button>
              <PayAssurance />
            </div>
          </>
        )}
      </div>
      <ChangeSheet open={changing} purpose="change" billing={b} onClose={() => setChanging(false)} onChanged={props.onChanged} />
      <AgreementSheet doc={doc} onClose={() => setDoc(null)} />
        </>
      ) : null}
    </div>
  );
}

/**
 * Under the button that pays, one quiet line: who takes the payment and the guarantee, each with its mark (owner, 2026-10-27,
 * of the two loose lines above the button: "look like they were just stuffed in here"). `stripe` false: nothing is paid here
 * (a plan paid already); `guarantee` false: a form that only saves a payment method.
 */
export function PayAssurance(props: { stripe?: boolean; guarantee?: string | false }) {
  const guarantee = props.guarantee === undefined ? "30-day money-back guarantee" : props.guarantee;
  return (
    <p class="bill-assure">
      {props.stripe === false ? null : (
        <span>
          <Icon name="lock" size="xs" />
          Payments by Stripe
        </span>
      )}
      {guarantee ? (
        <span>
          <Icon name="shield" size="xs" />
          {guarantee}
        </span>
      ) : null}
    </p>
  );
}

// ------------------------------------------------------------------------------------------- small deposits

/**
 * A bank account added by hand: the code on its small deposit (or its two amounts) confirms it. Before a subscription,
 * confirming it starts the toggle's plan (`start`; the button says what it pays); with one standing, it only becomes the
 * method.
 */
export function DepositCode(props: { billing: BillingOn; onChanged: () => Promise<void>; start?: BillingSubscribeRequest; button?: string }) {
  const v = props.billing.verification!;
  const [code, setCode] = useState("");
  const [amounts, setAmounts] = useState(["", ""]);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const by = v.arrivesBy ? formatDate(v.arrivesBy, "long") : null;
  const starts = props.start ?? null;

  const confirm = async (e?: Event) => {
    e?.preventDefault();
    if (busy) return;
    setFieldError(null);
    let body: BillingVerifyRequest;
    if (v.kind === "code") {
      const typed = code.trim().toUpperCase();
      if (!/^SM[A-Z0-9]{4}$/.test(typed)) return setFieldError("Enter the 6 characters that start with SM.");
      body = { code: typed };
    } else {
      const cents = amounts.map((a) => Math.round(Number(a)));
      if (cents.some((n) => !(n >= 1 && n <= 99))) return setFieldError("Enter each amount in cents, like 32.");
      body = { amounts: [cents[0]!, cents[1]!] };
    }
    setBusy(true);
    try {
      const done = await api.billingVerify(body);
      if (done.result === "ready" && starts) {
        await api.billingSubscribe(starts);
        toast("Your subscription has started.");
      } else toast(savedLine(done));
      await props.onChanged();
    } catch (x) {
      if (x instanceof ApiError && (x.fields?.code || x.fields?.amounts)) setFieldError(x.fields.code ?? x.fields.amounts ?? null);
      else {
        toast(failMessage(x));
        await props.onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="bill-verify">
      <h3 class="bill-sub-h">Confirm your bank account</h3>
      <p class="t-body">
        {v.kind === "code"
          ? `A deposit of $0.01 arrives${by ? ` by ${by}` : " in 1 to 2 business days"}. Its description has a code that starts with SM.`
          : `Two small deposits arrive${by ? ` by ${by}` : " in 1 to 2 business days"}. Enter both amounts.`}
      </p>
      <form class="bill-code" onSubmit={(e) => void confirm(e)} noValidate>
        {v.kind === "code" ? (
          <Field label="Code" error={fieldError ?? undefined}>
            {(c) => (
              <TextInput
                {...c}
                value={code}
                maxLength={6}
                autoComplete="off"
                autoCapitalize="characters"
                spellcheck={false}
                placeholder="SM"
                onInput={(e) => setCode((e.currentTarget as HTMLInputElement).value)}
              />
            )}
          </Field>
        ) : (
          <div class="bill-cents">
            {[0, 1].map((i) => (
              <Field key={i} label={i === 0 ? "First, in cents" : "Second, in cents"} error={i === 1 ? (fieldError ?? undefined) : undefined}>
                {(c) => (
                  <TextInput
                    {...c}
                    value={amounts[i]}
                    inputMode="numeric"
                    maxLength={2}
                    onInput={(e) => {
                      const next = [...amounts];
                      next[i] = (e.currentTarget as HTMLInputElement).value;
                      setAmounts(next);
                    }}
                  />
                )}
              </Field>
            ))}
          </div>
        )}
        <div class="bill-go">
          <Button type="submit" size="lg" busy={busy}>
            {starts ? (props.button ?? "Confirm and Start Subscription") : "Confirm Bank Account"}
          </Button>
          <LinkButton onClick={() => setChanging(true)}>Use Another Method</LinkButton>
        </div>
      </form>
      <ChangeSheet open={changing} purpose="change" billing={props.billing} onClose={() => setChanging(false)} onChanged={props.onChanged} />
    </div>
  );
}

// ------------------------------------------------------------------------------------------------- subscribed

export function MethodRow(props: { pm: BillingPaymentMethod | null; onChange: () => void }) {
  const pm = props.pm;
  return (
    <div class="bill-method">
      <span class="bill-l">
        <b>{pm ? methodName(pm) : "No payment method"}</b>
        <span>{pm ? methodMeta(pm) : "Add one to keep paying"}</span>
      </span>
      <LinkButton onClick={props.onChange}>{pm ? "Change" : "Add"}</LinkButton>
    </div>
  );
}

const STATE_PILL: Partial<Record<BillingState, string>> = { active: "Active", problem: "Payment Problem", canceling: "Canceling", pending: "Processing" };

function SubscribedView(props: { billing: BillingOn; state: BillingState; onChanged: () => Promise<void> }) {
  const b = props.billing;
  const { state } = props;
  const [changing, setChanging] = useState(false);
  const [asking, setAsking] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [busy, setBusy] = useState<"cancel" | "resume" | "refund" | "open" | null>(null);
  const currency = b.prices?.currency ?? "usd";
  const lastDay = b.currentPeriodEnd ? formatDate(b.currentPeriodEnd, "long") : null;
  const yearly = b.plan?.interval === "year";
  // A quote's first-charge date: the subscription is trialing and nothing is paid until that day (its period end), so there is no guarantee to
  // refund yet and a cancel ends it on that day with nothing charged.
  const trialing = b.status === "trialing";
  const guaranteeUntil = b.service.refundable ? b.service.guaranteeUntil : null;
  // While free there is no status to show ("Active" read as paid): First payment says it, as the quote did.
  const pill = trialing && state === "active" ? null : (STATE_PILL[state] ?? titleCase(String(b.status ?? "").replace(/_/g, " ")));
  // A yearly plan's end, said the way the cancel dialog says it.
  const endsLine = lastDay ? (yearly ? `Your plan ends on ${lastDay}.` : `Your subscription ends on ${lastDay}.`) : "Your subscription is set to end.";

  const setCanceling = async (cancel: boolean) => {
    setBusy(cancel ? "cancel" : "resume");
    try {
      if (cancel) await api.billingCancel();
      else await api.billingResume();
      toast(cancel ? endsLine : "Your subscription continues.");
      setAsking(false);
      await props.onChanged();
    } catch (e) {
      toast(failMessage(e));
    } finally {
      setBusy(null);
    }
  };

  // Paid, but the builder did not open (the payment went through and opening did not): Activate opens it.
  const unopened = b.service.state === "preview";
  const openNow = async () => {
    setBusy("open");
    try {
      await api.goLive({});
      if (site.value) site.value = { ...site.value, status: "live" };
      toast("You're open to families.");
      await props.onChanged();
    } catch (e) {
      toast(failMessage(e));
    } finally {
      setBusy(null);
    }
  };

  // The money-back guarantee: cancel now and the first payment goes back in full.
  const cancelAndRefund = async () => {
    setBusy("refund");
    try {
      await api.billingCancelRefund();
      toast("Your plan is cancelled. Your refund is on its way.");
      setRefunding(false);
      await props.onChanged();
    } catch (e) {
      toast(failMessage(e));
      setRefunding(false);
      await props.onChanged();
    } finally {
      setBusy(null);
    }
  };

  const problem = b.problem;
  const next = b.nextCharge;
  const p: BillingPlan | null = b.plan;
  return (
    <>
      {unopened ? (
        <div class="bill-alert is-quiet" role="status">
          <Icon name="info" />
          <div class="bill-alert-b">
            <b>Your plan is paid.</b>
            <span>Families can't start a board until you activate.</span>
          </div>
          <Button onClick={() => void openNow()} busy={busy === "open"}>
            Activate
          </Button>
        </div>
      ) : null}
      {guaranteeUntil ? (
        <p class="bill-guarantee">
          <Icon name="cal" size="sm" />
          <b>30-day money-back guarantee</b>
          <span>until {formatDate(guaranteeUntil, "long")}</span>
        </p>
      ) : null}
      {state === "problem" ? (
        <div class="bill-alert" role="alert">
          <Icon name="info" />
          <div class="bill-alert-b">
            <b>
              {problem
                ? `The ${formatDate(problem.date, "short")} payment of ${money(problem.amount, currency)} didn't go through.`
                : "The last payment didn't go through."}
            </b>
            <span>{problem?.reason ?? "Update the payment method and it's tried again."}</span>
          </div>
          <Button onClick={() => setChanging(true)}>Update Payment Method</Button>
        </div>
      ) : null}
      {state === "canceling" ? (
        <div class="bill-alert is-quiet">
          <Icon name="cal" />
          <div class="bill-alert-b">
            <b>{endsLine}</b>
            <span>{trialing ? "Nothing is charged." : yearly ? "You won't be charged again." : "Nothing is charged after that."}</span>
          </div>
          <Button onClick={() => void setCanceling(false)} busy={busy === "resume"}>
            Resume Subscription
          </Button>
        </div>
      ) : null}
      {b.verification ? (
        <div class="bill-verify-sec">
          <DepositCode billing={b} onChanged={props.onChanged} />
        </div>
      ) : null}
      <ul class="bill-rows">
        <Row
          label="Plan"
          value={p ? planName(p.id) : (b.prices?.name ?? "Subscription")}
          sub={p ? rateWords(p.interval, p.amount, currency) : undefined}
          extra={
            p?.kind === "founding" ? (
              <span class="bill-locked">
                <Icon name="lock" size="xs" />
                Your rate is locked
              </span>
            ) : null
          }
          end={pill ? <Pill tone="line">{pill}</Pill> : undefined}
        />
        {state === "canceling" ? (
          <Row label="Last day" value={lastDay ?? "At the end of this period"} sub="No more charges" />
        ) : trialing && lastDay ? (
          <Row
            label="First payment"
            value={`Free until ${formatDate(next?.date ?? b.currentPeriodEnd!, "long")}`}
            sub={next || p ? `Then ${afterFree(next, p?.amount ?? 0, yearly ? "year" : "month", currency)}` : undefined}
          />
        ) : next ? (
          <Row
            label={yearly ? "Renews" : "Next charge"}
            value={formatDate(next.date, "long")}
            sub={`${money(next.total, currency)}${next.tax ? `, with ${money(next.tax, currency)} tax` : ""}`}
          />
        ) : state === "pending" ? (
          <Row label="First payment" value="On its way" sub="Bank payments take a few business days" />
        ) : null}
        <li class="bill-method-row">
          <span class="bill-k">Payment method</span>
          <MethodRow pm={b.paymentMethod} onChange={() => setChanging(true)} />
        </li>
      </ul>
      <Invoices invoices={b.invoices} failed={state === "problem" ? (b.invoices.find((i) => i.status === "open")?.id ?? null) : null} />
      {guaranteeUntil ? (
        <div class="bill-cancel">
          <button type="button" class="link-btn bill-cancel-btn" onClick={() => setRefunding(true)}>
            Cancel and Refund
          </button>
        </div>
      ) : state !== "canceling" ? (
        <div class="bill-cancel">
          <button type="button" class="link-btn bill-cancel-btn" onClick={() => setAsking(true)}>
            Cancel Subscription
          </button>
        </div>
      ) : null}
      <Dialog
        open={refunding}
        onClose={() => setRefunding(false)}
        title="Cancel and Refund?"
        width={480}
        class="ts-dialog bill-dialog"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRefunding(false)}>
              Keep Plan
            </Button>
            <Button class="btn-danger" onClick={() => void cancelAndRefund()} busy={busy === "refund"}>
              Cancel and Refund
            </Button>
          </>
        }
      >
        <p class="t-body">
          Your plan is cancelled now and your payment is refunded in full. It can take 5 to 10 business days to show. New families can't
          start a board. Families already in keep theirs for 30 days.
        </p>
      </Dialog>
      <ChangeSheet
        open={changing}
        purpose="change"
        billing={b}
        onClose={() => setChanging(false)}
        onChanged={props.onChanged}
        retryAmount={state === "problem" ? (problem?.amount ?? null) : null}
      />
      <Dialog
        open={asking}
        onClose={() => setAsking(false)}
        title="Cancel Subscription?"
        width={480}
        class="ts-dialog bill-dialog"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAsking(false)}>
              Keep Subscription
            </Button>
            <Button class="btn-danger" onClick={() => void setCanceling(true)} busy={busy === "cancel"}>
              Cancel Subscription
            </Button>
          </>
        }
      >
        {trialing && lastDay ? (
          <p class="t-body">
            Your free time ends on <b>{lastDay}</b>, and your plan ends with it. Nothing is charged.
          </p>
        ) : yearly && lastDay ? (
          <p class="t-body">
            Your plan ends on <b>{lastDay}</b>. You won't be charged again.
            {p?.kind === "founding" ? " Your founding rate ends with it." : ""}
          </p>
        ) : (
          <p class="t-body">
            {lastDay ? (
              <>
                Your last day is <b>{lastDay}</b>.
              </>
            ) : (
              "It ends at the end of this billing period."
            )}{" "}
            Nothing is charged after that. You can resume any time before then.
          </p>
        )}
      </Dialog>
    </>
  );
}

/** The invoices, newest first. `failed`: the open invoice whose payment didn't go through ("Not paid", not "Pending"). */
function Invoices(props: { invoices: BillingInvoice[]; failed: string | null }) {
  if (!props.invoices.length) return null;
  return (
    <div class="bill-inv">
      <h3 class="bill-sub-h">Invoices</h3>
      <ul class="bill-inv-list">
        {props.invoices.map((inv) => (
          <li key={inv.id}>
            <span class="bill-inv-d">{formatDate(inv.date, "medium")}</span>
            <span class="bill-inv-n">{inv.number ?? ""}</span>
            <span class="bill-inv-a">{money(inv.amount, inv.currency)}</span>
            <span class={cx("bill-inv-s", inv.id === props.failed && "is-failed")}>{inv.id === props.failed ? "Not paid" : INVOICE_STATUS[inv.status]}</span>
            <span class="bill-inv-x">
              {inv.viewUrl ? (
                <a class="link-btn" href={inv.viewUrl} target="_blank" rel="noopener noreferrer" aria-label={`View the ${formatDate(inv.date, "medium")} invoice`}>
                  <Icon name="eye" size="sm" />
                  <span>View</span>
                </a>
              ) : null}
              {inv.pdfUrl ? (
                <a class="link-btn" href={inv.pdfUrl} target="_blank" rel="noopener noreferrer" aria-label={`Download the ${formatDate(inv.date, "medium")} invoice`}>
                  <Icon name="download" size="sm" />
                  <span>Download</span>
                </a>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ------------------------------------------------------------------------------------------ change the method

/**
 * The payment form in a side panel (a bottom sheet on a phone). With a payment problem it says what it will pay: once
 * the new method is saved the server pays the open invoice with it.
 */
export function ChangeSheet(props: {
  open: boolean; purpose: BillingSetupPurpose; billing: BillingOn; onClose: () => void; onChanged: () => Promise<void>; retryAmount?: number | null;
}) {
  const handle = useRef<PaymentFormHandle | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (props.open) setError(null);
  }, [props.open]);
  const currency = props.billing.prices?.currency ?? "usd";

  const save = async () => {
    const form = handle.current;
    if (busy || !form) return;
    setBusy(true);
    setError(null);
    try {
      const confirmed = await form.confirm();
      if (!confirmed.ok) {
        setError(confirmed.message);
        return;
      }
      const done = await api.billingComplete({ setupIntentId: confirmed.setupIntentId, address: confirmed.address });
      toast(savedLine(done));
      props.onClose();
      await props.onChanged();
    } catch (e) {
      setError(failMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={props.open}
      onClose={props.onClose}
      title="Change Payment Method"
      class="bill-sheet"
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} busy={busy}>
            {props.retryAmount ? `Save and Pay ${money(props.retryAmount, currency)}` : "Save Payment Method"}
          </Button>
        </>
      }
    >
      {props.open ? <PaymentForm purpose={props.purpose} address={props.billing.address} onHandle={(h) => (handle.current = h)} /> : <Skeleton lines={6} />}
      {error ? (
        <p class="bill-err" role="alert">
          {error}
        </p>
      ) : null}
      <PayAssurance guarantee={false} />
    </Sheet>
  );
}
